/**
 * Maps stored or playbook tool references to the ids of the tools the catalog lists now (`/ai-tools` items):
 * an id as is, a display name (old format), a legacy `category:name`, or a snake_case name (old ADK) in kebab-case.
 * References that match nothing are dropped. Order is kept, duplicates removed.
 * @param {string[]} rawIds
 * @param {{ id: string, name?: string }[]} tools
 * @returns {string[]}
 */
export function resolveToolIds(rawIds, tools) {
  if (!Array.isArray(rawIds) || rawIds.length === 0) { return []; }
  const knownIds = new Set(tools.map((tool) => tool.id));
  const nameToId = new Map(tools.map((tool) => [tool.name, tool.id]));
  const toKebab = (value) => value.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
  return [...new Set(rawIds.flatMap((rawId) => {
    if (typeof rawId !== 'string') { return []; }
    if (knownIds.has(rawId)) { return [rawId]; }
    if (nameToId.has(rawId)) { return [nameToId.get(rawId)]; }
    const sep = rawId.indexOf(':');
    const stripped = sep >= 0 ? rawId.substring(sep + 1) : rawId;
    if (knownIds.has(stripped)) { return [stripped]; }
    if (nameToId.has(stripped)) { return [nameToId.get(stripped)]; }
    const kebab = toKebab(stripped);
    if (knownIds.has(kebab)) { return [kebab]; }
    const kebabRaw = toKebab(rawId);
    if (knownIds.has(kebabRaw)) { return [kebabRaw]; }
    return [];
  }))];
}

/**
 * The stored tool selection checked against the tool list that just loaded: references migrated to today's ids
 * (`resolveToolIds`) and disabled tools dropped. `null` when nothing in it is a tool anymore, which makes the caller
 * fall back to the default selection (and save it, so the next load finds a valid one).
 * @param {string[]|null} stored
 * @param {{ id: string, name?: string, enabled?: boolean }[]} tools
 * @returns {string[]|null}
 */
export function reconcileStoredToolSelection(stored, tools) {
  if (!Array.isArray(stored)) { return stored ?? null; }
  if (stored.length === 0) { return stored; }
  const resolved = resolveToolIds(stored, tools);
  if (resolved.length === 0) { return null; }
  const enabled = new Set(tools.filter((tool) => tool.enabled !== false).map((tool) => tool.id));
  return resolved.filter((id) => enabled.has(id));
}
